// IndexedDB setup for offline-first architecture
import Dexie from 'dexie';

class WanderersWalletDB extends Dexie {
  constructor() {
    super('WanderersWalletDB');
    
    this.version(3).stores({
      expenses: '++id, amount, currency, category, note, date, synced, deleted',
      trips: '++id, name, startDate, endDate, totalBudget, synced',
      settings: 'key, value',
      exchangeRates: 'currency, rate, lastUpdated',
      syncQueue: '++id, operation, collection, data, timestamp',
      ious: '++id, friendName, amount, currency, note, date, paidBack',
      phraseFavorites: '++id, language, phraseId, phrase, translation, pronunciation, category'
    });

    this.expenses = this.table('expenses');
    this.trips = this.table('trips');
    this.settings = this.table('settings');
    this.exchangeRates = this.table('exchangeRates');
    this.syncQueue = this.table('syncQueue');
    this.ious = this.table('ious');
    this.phraseFavorites = this.table('phraseFavorites');
  }
}

export const db = new WanderersWalletDB();

// Helper function to add to sync queue
export const queueSync = async (operation, collection, data) => {
  await db.syncQueue.add({
    operation,
    collection,
    data,
    timestamp: new Date().toISOString()
  });
};

// Sync with server when online
export const syncWithServer = async (apiUrl) => {
  if (!navigator.onLine) return;

  try {
    const queueItems = await db.syncQueue.toArray();
    
    for (const item of queueItems) {
      try {
        // Sync each item with the server
        const response = await fetch(`${apiUrl}/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item)
        });

        if (response.ok) {
          // Remove from queue if successfully synced
          await db.syncQueue.delete(item.id);
          
          // Update local record as synced
          if (item.collection === 'expenses' && item.data.id) {
            await db.expenses.update(item.data.id, { synced: true });
          }
        }
      } catch (error) {
        console.error('Sync failed for item:', item, error);
      }
    }
  } catch (error) {
    console.error('Sync process failed:', error);
  }
};

// Fetch and cache exchange rates (bulk)
export const fetchExchangeRates = async (apiUrl) => {
  try {
    // Check if we have recent cached rates (less than 1 hour old)
    const cachedMeta = await db.settings.get('exchange_rates_meta');
    if (cachedMeta) {
      const lastUpdated = new Date(cachedMeta.value.lastUpdated);
      const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
      
      if (lastUpdated > hourAgo) {
        // Return cached rates
        const rates = await db.exchangeRates.toArray();
        return {
          rates: rates.reduce((acc, r) => ({ ...acc, [r.currency]: r.rate }), {}),
          source: cachedMeta.value.source,
          cached: true
        };
      }
    }

    // Fetch fresh rates from server
    const response = await fetch(`${apiUrl}/exchange-rates`);
    if (!response.ok) throw new Error('Failed to fetch rates');
    
    const data = await response.json();
    
    // Store rates in IndexedDB
    const ratesArray = Object.entries(data.rates || {}).map(([currency, rate]) => ({
      currency,
      rate,
      lastUpdated: data.last_updated
    }));
    
    await db.exchangeRates.bulkPut(ratesArray);
    
    // Store metadata
    await db.settings.put({
      key: 'exchange_rates_meta',
      value: {
        lastUpdated: data.last_updated,
        source: data.source,
        base: data.base
      }
    });
    
    return {
      rates: data.rates,
      source: data.source,
      cached: false
    };
  } catch (error) {
    console.error('Failed to fetch exchange rates:', error);
    
    // Return cached rates if available
    const rates = await db.exchangeRates.toArray();
    if (rates.length > 0) {
      return {
        rates: rates.reduce((acc, r) => ({ ...acc, [r.currency]: r.rate }), {}),
        source: 'offline-cache',
        cached: true
      };
    }
    
    throw error;
  }
};

// Get single exchange rate (from cache or API)
export const getExchangeRate = async (currency, apiUrl) => {
  // Try local cache first
  const cached = await db.exchangeRates.get(currency);
  if (cached) {
    const lastUpdated = new Date(cached.lastUpdated);
    const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
    
    if (lastUpdated > hourAgo) {
      return cached.rate;
    }
  }
  
  // Fetch from API
  try {
    const response = await fetch(`${apiUrl}/exchange-rates/${currency}`);
    if (!response.ok) throw new Error('Failed to fetch rate');
    
    const data = await response.json();
    
    // Cache it
    await db.exchangeRates.put({
      currency,
      rate: data.rate,
      lastUpdated: data.last_updated
    });
    
    return data.rate;
  } catch (error) {
    // Return cached rate if available
    if (cached) return cached.rate;
    throw error;
  }
};